import type { PanVerificationStatus } from "@entities/campaigns/lib/hooks/useVerifyPan";
import { countryCodeOptions } from "@shared/lib/constants";
import { ToastService } from "@shared/lib/services";
import { ICreateVendorBilling } from "@shared/lib/types/briefCampains";
import { Checkbox } from "@shared/ui/Checkbox";
import InputNumber from "@shared/ui/InputNumber";
import { Button, Col, Flex, Form, Input, Radio, Row, Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import type { BillingFormInitialValues } from "./lib/mapBillingEntityToFormValues";
import { PAN_PATTERN, PanVerifyField } from "./ui/PanVerifyField";

const { Option } = Select;

const DEFAULT_VALUES: BillingFormInitialValues = {
  contactType: "email",
  isInternational: false,
  isPan: false,
  isMsme: false,
  isGst: false,
  tdsPercentage: 10,
};

interface AddBillingDetailsProps {
  type: "Influencer" | "Agency";
  onAdd: (vendorBilling: ICreateVendorBilling) => void;
  loading: boolean;
  initialValues?: BillingFormInitialValues | null;
  initialPanStatus?: PanVerificationStatus | null;
}

export function AddBillingDetails({
  type,
  loading,
  onAdd,
  initialValues,
  initialPanStatus = null,
}: AddBillingDetailsProps) {
  const [form] = Form.useForm();
  const [panStatus, setPanStatus] = useState<PanVerificationStatus | null>(
    initialPanStatus
  );

  const mergedInitialValues = useMemo(
    () => ({
      ...DEFAULT_VALUES,
      ...initialValues,
    }),
    [initialValues]
  );

  useEffect(() => {
    form.setFieldsValue(mergedInitialValues);
    setPanStatus(initialPanStatus ?? null);
  }, [form, mergedInitialValues, initialPanStatus]);

  const contactType = Form.useWatch("contactType", form) || "email";
  const hasPan = Form.useWatch("isPan", form) || false;
  const hasMsme = Form.useWatch("isMsme", form) || false;
  const hasGst = Form.useWatch("isGst", form) || false;
  const panNumber = Form.useWatch("panNumber", form) || "";

  const canSave = !loading && (!hasPan || panStatus === "Valid");

  useEffect(() => {
    if (!hasPan) return;
    void form.validateFields(["panNumber"]).catch(() => undefined);
  }, [panStatus, hasPan, form]);

  const handleFinish = (values: ICreateVendorBilling) => {
    if (hasPan && panStatus !== "Valid") {
      ToastService.error("Please verify PAN before saving");
      return;
    }

    onAdd({
      ...values,
      type,
      tdsPercentage: values.tdsPercentage,
      gstPercentage: hasGst ? 18 : 0,
      ...(hasPan
        ? { panNumber: String(panNumber).trim().toUpperCase() }
        : {}),
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={mergedInitialValues}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
      onValuesChange={(changed) => {
        if ("isPan" in changed && !changed.isPan) {
          setPanStatus(null);
          form.setFieldValue("panNumber", undefined);
        }
      }}
    >
      <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {type === "Influencer" && (
          <>
            <Form.Item
              label="Choose Primary Contact Type"
              name="contactType"
              required
            >
              <Radio.Group>
                <Radio value="email">Email ID</Radio>
                <Radio value="phone">Phone Number</Radio>
              </Radio.Group>
            </Form.Item>
            {contactType === "email" ? (
              <Form.Item
                name="emailId"
                label="Email Id"
                rules={[
                  { required: true, message: "Email is required" },
                  { type: "email", message: "Enter a valid email" },
                ]}
              >
                <Input placeholder="Enter email" />
              </Form.Item>
            ) : (
              <Form.Item label="Phone Number">
                <Input.Group compact>
                  <Form.Item
                    name="countryCode"
                    noStyle
                    rules={[
                      { required: true, message: "Country code is required" },
                    ]}
                  >
                    <Select style={{ width: "30%" }}>
                      {countryCodeOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="phoneNumber"
                    noStyle
                    rules={[
                      { required: true, message: "Phone number is required" },
                      {
                        pattern: /^\d+$/,
                        message: "Phone number must be numeric",
                      },
                    ]}
                  >
                    <Input
                      style={{ width: "70%" }}
                      placeholder="Phone Number"
                    />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            )}
          </>
        )}
        {type === "Agency" && (
          <Form.Item
            name="emailId"
            label="Email Id"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>
        )}

        <Form.Item label="What's the billing type?" name="billingType" required>
          <Radio.Group>
            <Radio value="Individual">Individual</Radio>
            <Radio value="Company">Company</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="isInternational" valuePropName="checked">
          <Checkbox>Is the contact International?</Checkbox>
        </Form.Item>

        <Form.Item
          name="name"
          label={type === "Agency" ? "Company Name" : "Full Name"}
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input
            placeholder={
              type === "Agency" ? "Enter company name" : "Enter full name"
            }
          />
        </Form.Item>

        <Form.Item
          name="addressLine1"
          label="Street Address 1"
          rules={[{ required: true, message: "Street address is required" }]}
        >
          <Input placeholder="Flat No., Society, Street Number etc" />
        </Form.Item>
        <Form.Item
          name="addressLine2"
          label="Street Address 2"
          rules={[{ required: true, message: "Street address is required" }]}
        >
          <Input placeholder="Flat No., Society, Street Number etc" />
        </Form.Item>

        <Row gutter={8}>
          <Col span={12}>
            <Form.Item
              name="city"
              label="City"
              rules={[{ required: true, message: "City is required" }]}
            >
              <Input placeholder="City" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="state"
              label="State"
              rules={[{ required: true, message: "State is required" }]}
            >
              <Input placeholder="State" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={8}>
          <Col span={12}>
            <Form.Item
              name="country"
              label="Country"
              rules={[{ required: true, message: "Country is required" }]}
            >
              <Input placeholder="Country" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="pinCode"
              label="Pin Code"
              rules={[{ required: true, message: "Pin code is required" }]}
            >
              <Input placeholder="Pin Code" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="isPan" valuePropName="checked">
          <Checkbox>Does the contact have Pan Card?</Checkbox>
        </Form.Item>
        {hasPan && (
          <Form.Item
            name="panNumber"
            label="PAN Card Number"
            rules={[
              { required: true, message: "PAN number is required" },
              {
                pattern: PAN_PATTERN,
                message: "Enter a valid PAN (e.g. ABCDE1234F)",
              },
              {
                validator: async () => {
                  if (panStatus !== "Valid") {
                    return Promise.reject(
                      new Error("Please verify PAN to continue")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            style={{ marginBottom: 16 }}
          >
            <PanVerifyField status={panStatus} onStatusChange={setPanStatus} />
          </Form.Item>
        )}
        <Form.Item
          name="tdsPercentage"
          label="TDS Percentage"
          rules={[{ required: true, message: "TDS percentage is required" }]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber placeholder="TDS Percentage" addonAfter={"%"} />
        </Form.Item>

        <Form.Item name="isMsme" valuePropName="checked">
          <Checkbox>Does the contact have MSME?</Checkbox>
        </Form.Item>
        {hasMsme && (
          <Form.Item
            name="msmeNumber"
            label="MSME Number"
            rules={[{ required: true, message: "MSME number is required" }]}
          >
            <Input placeholder="MSME Number" />
          </Form.Item>
        )}

        <Form.Item name="isGst" valuePropName="checked">
          <Checkbox>Does the contact have GST?</Checkbox>
        </Form.Item>
        {hasGst && (
          <Flex vertical gap={16} style={{ marginBottom: 16 }}>
            <Form.Item
              name="gstNumber"
              label="GST Number"
              rules={[{ required: true, message: "GST number is required" }]}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder="GST Number" addonAfter={`Fixed GST: 18% `} />
            </Form.Item>
          </Flex>
        )}
      </div>

      <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={!canSave}
        >
          Save
        </Button>
      </Form.Item>
    </Form>
  );
}
